import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Layers, Clock, DollarSign, FileCheck, RefreshCw, TrendingUp } from 'lucide-react';

const HybridLOC = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/3184306/pexels-photo-3184306.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-violet-950/90 via-slate-900/85 to-slate-900/70" />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
              <Layers className="h-4 w-4 text-violet-300" />
              <span className="text-xs font-semibold text-violet-200 uppercase tracking-wider">Term Loan / Line of Credit Hybrid</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Term Loan<br /><span className="text-violet-300">LOC Hybrid</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
              The flexibility of a line of credit with the scale of a term loan. Access up to $25M, draw what you need, and only pay interest on what you use. Close and fund in as little as 1 day.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/apply" data-testid="hybrid-apply" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-100 transition-all">
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">Talk to Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats Strip */}
      <section className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <DollarSign className="h-5 w-5 text-violet-400" />, value: 'Up to $25M', label: 'Credit Facility' },
              { icon: <Clock className="h-5 w-5 text-violet-400" />, value: '1 Day', label: 'Close and Fund' },
              { icon: <RefreshCw className="h-5 w-5 text-violet-400" />, value: 'Interest Only', label: 'On What You Use' },
              { icon: <Layers className="h-5 w-5 text-violet-400" />, value: 'Hybrid', label: 'Term + Revolving' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Details Grid */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Program Details</h2>
              <p className="text-slate-500 mb-10">A hybrid structure that combines the committed capital of a term loan with the draw flexibility of a revolving line of credit. Only pay interest on the funds you actually deploy.</p>
              {[
                { label: 'Facility Size', value: 'Up to $25,000,000' },
                { label: 'Structure', value: 'Term Loan / LOC Hybrid' },
                { label: 'Interest', value: 'Pay only on what you draw' },
                { label: 'Closing Timeline', value: 'As fast as 1 business day' },
                { label: 'Draw Flexibility', value: 'Draw and repay as needed' },
                { label: 'Documentation', value: 'Streamlined process' },
              ].map((item) => (
                <div key={item.label} className="flex justify-between items-center py-4 border-b border-slate-200">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{item.label}</span>
                  <span className="text-sm font-bold text-slate-900">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="space-y-8">
              {/* Image card */}
              <div className="relative rounded-2xl overflow-hidden h-56">
                <img src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Team" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-violet-950/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>Pay Only for What You Use</div>
                  <p className="text-xs text-violet-100/80 mt-1">Interest accrues only on the funds you draw, not on the full committed facility. Maximum flexibility, minimum cost.</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Ideal Use Cases</h3>
                {[
                  'Ongoing working capital with variable demand',
                  'Seasonal businesses with fluctuating cash needs',
                  'Acquisition pipelines requiring staged funding',
                  'Real estate development draws',
                  'Inventory purchasing with rotating cycles',
                  'Multi project businesses needing flexible capital',
                ].map((use) => (
                  <div key={use} className="flex items-center gap-3 mb-4 last:mb-0">
                    <CheckCircle className="h-5 w-5 text-violet-600 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{use}</span>
                  </div>
                ))}
              </div>

              <Link to="/apply" className="block bg-violet-600 text-white rounded-2xl p-8 group hover:bg-violet-500 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Get Started Today</h3>
                    <p className="text-sm text-violet-200">Close in as little as 1 day</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-white group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>How the Hybrid Works</h2>
            <p className="text-slate-500 max-w-xl mx-auto">The best of both worlds: committed capital with revolving flexibility.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <FileCheck className="h-6 w-6" />, title: 'Get Approved', desc: 'Apply once and get approved for your full facility amount. Streamlined documentation, fast decisions.', color: 'bg-violet-50 text-violet-700' },
              { icon: <DollarSign className="h-6 w-6" />, title: 'Draw What You Need', desc: 'Access capital when you need it. Draw the full amount or just what a specific opportunity requires.', color: 'bg-emerald-50 text-emerald-700' },
              { icon: <TrendingUp className="h-6 w-6" />, title: 'Repay and Redraw', desc: 'As you repay, your available balance replenishes. Interest is only charged on outstanding draws.', color: 'bg-sky-50 text-sky-700' },
            ].map((card) => (
              <div key={card.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-6`}>
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{card.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Capital on Your Terms</h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">Access up to $25M with the flexibility to draw only what you need. Pay interest only on what you use.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-violet-500 transition-colors">Apply Now <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HybridLOC;
